import { useState, useCallback } from 'react';

/**
 * useLoading - Custom hook to manage loading overlay state.
 *
 * Returns:
 *   - isLoading: boolean indicating if any async operation is in progress.
 *   - withLoading: wrapper function that sets loading to true before the async
 *     operation and resets it in a finally block (covers both success and error).
 *
 * Usage:
 *   const { isLoading, withLoading } = useLoading();
 *
 *   const handleSubmit = (e) => withLoading(async () => {
 *     e.preventDefault();
 *     await apiService.post(...);
 *   });
 */
const useLoading = () => {
  const [loadingCount, setLoadingCount] = useState(0);

  const withLoading = useCallback((asyncFn) => {
    return async (...args) => {
      setLoadingCount(prev => prev + 1);
      try {
        return await asyncFn(...args);
      } finally {
        setLoadingCount(prev => prev - 1);
      }
    };
  }, []);

  return {
    isLoading: loadingCount > 0,
    withLoading
  };
};

export default useLoading;
